import {
  ApiChartAttribute,
  ApiDimension,
  ApiExternalEntityStatus,
  ApiMetric,
  ApiOperator,
  ApiUTMEntity,
} from "@incendium/api";
import {
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  capitalize,
  styled,
} from "@mui/material";
import { IChart } from "Interfaces";
import { AdStatCard } from "features/attributionAnalytics";
import { useMetaUtmEntites } from "features/dataConnectors/hooks/useMetaUtmEntites";
import { useChartData } from "features/analytics";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import Loading from "Components/Loading/Loading";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Facebook,
  GridView,
  Instagram,
  PauseCircleOutline,
  TableView,
} from "@mui/icons-material";
import { Pagination } from "features/pagination";
import GlassCard from "Components/GlassCard/GlassCard";
import AdStatsTable from "features/attributionAnalytics/components/AdStatsTable";
import { IAdStat } from "features/attributionAnalytics/types";

const SortBy = {
  [ApiMetric.METRIC_PUBLISHER_CLICKS]: "clicks",
  [ApiMetric.METRIC_SESSIONS_COUNT]: "sessions",
  [ApiMetric.METRIC_ROAS]: "roas",
  [ApiMetric.METRIC_PUBLISHER_ROAS]: "publisher roas",
};

const StyledPauseButton = styled(IconButton)<{ active: boolean }>(
  ({ active }) => ({
    position: "relative",
    "&:after": {
      position: "absolute",
      bottom: 0,
      right: 0,
      content: active ? "'\u2713'" : "''",
      display: "block",
      fontSize: "15px",
      fontWeight: "bold",
    },
  }),
);

function AdsOverviewPage() {
  const { entites } = useMetaUtmEntites();
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  const [showPaused, setShowPaused] = useState(false);
  const [view, setView] = useState<"table" | "card">("card");
  const [publisher, setPublisher] = useState<
    "facebook" | "instagram" | "audience_network" | "meta" | ""
  >("meta");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<keyof typeof SortBy>(
    ApiMetric.METRIC_PUBLISHER_CLICKS,
  );
  const [search, setSearch] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const filters: ApiChartAttribute[] = useMemo(() => {
    if (
      publisher === "meta" ||
      publisher === "" ||
      publisher === "audience_network"
    ) {
      return [];
    }
    return [
      {
        key: ApiDimension.DIMENSION_SOURCE,
        operator: ApiOperator.EQUAL,
        value: publisher,
      },
    ];
  }, [publisher]);

  const chart: IChart = useMemo(
    () => ({
      name: "Ads Overview",
      dimension: [ApiDimension.DIMENSION_EXTERNAL_AD_ID],
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_SPEND,
            ApiMetric.METRIC_PUBLISHER_CLICKS,
            ApiMetric.METRIC_PUBLISHER_CPC,
            ApiMetric.METRIC_PUBLISHER_ROAS,
            ApiMetric.METRIC_SESSIONS_COUNT,
            ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
            ApiMetric.METRIC_MACRO_CONVERSION_RATE,
            ApiMetric.METRIC_ROAS,
            ApiMetric.METRIC_PUBLISHER_CLICKS_DIFFERENCE,
            ApiMetric.METRIC_PUBLISHER_SALES_COUNT,
            ApiMetric.METRIC_SALES_COUNT,
          ],
        },
      ],
      attributes: filters,
    }),
    [filters],
  );

  const { chartData, loading } = useChartData(
    selectedProject,
    chart,
    chartOutput,
  );

  const adData = useCallback(
    (ad: ApiUTMEntity) => {
      const data = (chartData?.data || []).find(
        (data) => data.name === String(ad.id),
      );
      return (
        data || {
          name: "",
        }
      );
    },
    [chartData],
  );

  const ads: IAdStat[] = useMemo(() => {
    const filteredAds = entites
      .map((ad) => ({
        ...ad,
        data: adData(ad),
      }))
      .filter(
        (ad) =>
          ad.data.name !== "" &&
          Number(ad.data[ApiMetric.METRIC_PUBLISHER_CLICKS] || 0) > 0,
      )
      .filter((ad) => {
        if (search.length < 3) {
          return true;
        }
        return (ad.name || "").toLowerCase().includes(search.toLowerCase());
      })
      .filter((ad) =>
        showPaused
          ? true
          : ad.entityStatus !==
            ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_PAUSED,
      );
    return filteredAds.sort((a, b) => {
      return Number(b.data[sortBy]) - Number(a.data[sortBy]);
    });
  }, [entites, adData, showPaused, sortBy, search]);

  const paginatedAds = useMemo(() => {
    return (ads || []).slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [ads, page, itemsPerPage]);

  const publisherName = useMemo(() => {
    return !publisher || publisher === "meta"
      ? "Meta"
      : capitalize(publisher || "");
  }, [publisher]);

  useEffect(() => {
    setPage(1);
  }, [sortBy, itemsPerPage]);

  if (loading) return <Loading fullHeight />;

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ height: "100%" }}
        direction={view === "card" ? "row" : "column"}
      >
        <Grid item sx={{ width: "100%" }}>
          <GlassCard>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <TextField
                  sx={{ width: 150 }}
                  label="Search"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <TextField
                  sx={{ width: 150 }}
                  label="Sort By"
                  size="small"
                  select
                  value={sortBy}
                  onChange={(e) =>
                    setSortBy(e.target.value as keyof typeof SortBy)
                  }
                >
                  <MenuItem value={ApiMetric.METRIC_PUBLISHER_CLICKS}>
                    Clicks
                  </MenuItem>
                  <MenuItem value={ApiMetric.METRIC_SESSIONS_COUNT}>
                    Sessions
                  </MenuItem>
                  <MenuItem value={ApiMetric.METRIC_ROAS}>ROAS</MenuItem>
                  <MenuItem value={ApiMetric.METRIC_PUBLISHER_ROAS}>
                    Publisher ROAS
                  </MenuItem>
                </TextField>

                <Tooltip title="View metrics by publisher">
                  <ToggleButtonGroup
                    exclusive
                    color="primary"
                    size="small"
                    value={publisher}
                    onChange={(e, v) => setPublisher(v)}
                  >
                    <ToggleButton value="facebook">
                      <Facebook />
                    </ToggleButton>
                    <ToggleButton value="instagram">
                      <Instagram />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
                <Tooltip title="View as card or table">
                  <ToggleButtonGroup
                    exclusive
                    color="primary"
                    size="small"
                    value={view}
                    onChange={(e, v) => !!v && setView(v)}
                  >
                    <ToggleButton value="card">
                      <GridView />
                    </ToggleButton>
                    <ToggleButton value="table">
                      <TableView />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
                <Tooltip
                  title={showPaused ? "Hide paused ads" : "Show paused ads"}
                >
                  <StyledPauseButton
                    active={showPaused}
                    onClick={() => setShowPaused(!showPaused)}
                    size="small"
                    color={showPaused ? "primary" : "default"}
                  >
                    <PauseCircleOutline />
                  </StyledPauseButton>
                </Tooltip>
              </Stack>

              {view === "card" && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    sx={{ width: 100 }}
                    label="Items per page"
                    size="small"
                    select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <MenuItem value={6}>6</MenuItem>
                    <MenuItem value={12}>12</MenuItem>
                    <MenuItem value={24}>24</MenuItem>
                    <MenuItem value={48}>48</MenuItem>
                  </TextField>
                  <Pagination
                    totalPages={ads.length}
                    page={page}
                    onChange={(p) => setPage(p)}
                  />
                </Stack>
              )}
            </Stack>
          </GlassCard>
        </Grid>

        {view === "table" ? (
          <Grid item sx={{ flexGrow: 1, minHeight: 0 }}>
            <AdStatsTable ads={ads} publisherName={publisherName} />
          </Grid>
        ) : (
          paginatedAds.map((ad) => (
            <Grid item xs={4} key={ad.id}>
              <AdStatCard
                project={selectedProject!}
                ad={ad}
                publisherName={publisherName}
                data={ad.data}
                filters={filters}
              />
            </Grid>
          ))
        )}
      </Grid>
    </>
  );
}

export default AdsOverviewPage;
